import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { ApiError } from '../../api.js';
import { db } from '../../db/local.js';
import type { LocalHouseholdMember } from '../../db/local.js';
import { readBoardRewards, writeBoardRewards } from '../../db/familyDisplay.js';
import { createInviteCode, joinHousehold, leaveHousehold, listMembers } from '../../db/shopping.js';
import { useIsChild } from '../../hooks/useModules.js';
import { ChildrenSection } from './Children.js';

/**
 * Wer teilt sich Liste und Vorrat?
 *
 * Beitreten und Austreten gehen nur online — mit Absicht. Eine Mitgliedschaft,
 * die offline „geklappt" hätte und später abgelehnt wird, wäre eine Liste, die
 * man für geteilt hält und die es nicht ist.
 */
export function HouseholdPanel({
  householdId,
  onChanged,
  onOpenFamilyDisplay,
}: {
  householdId: string | null;
  onChanged: () => void;
  /** Familienanzeige (27.10.2026) — nur Erwachsene, nur mit Haushalt. */
  onOpenFamilyDisplay?: (() => void) | undefined;
}): React.JSX.Element {
  const { t } = useTranslation();
  const members = useLiveQuery(
    () => listMembers(householdId, db),
    [householdId],
    [] as LocalHouseholdMember[],
  );
  // Kinder (E16): sehen, wer dazugehört — verwalten, einladen, austreten nicht.
  const child = useIsChild();
  const kids = members.filter((member) => member.role === 'child');
  // Wie der Server: Kinder zählen nicht als „andere“. Der letzte Erwachsene
  // geht nicht und lässt Kinder allein zurück.
  const alone = members.length - kids.length <= 1;

  return (
    <section aria-labelledby="household-heading" className="flex flex-col gap-3">
      <div className="kw-tile">
        <h2 id="household-heading" className="kw-kicker">
          {t('shopping.household.heading')}
        </h2>
        <p className="mt-1 text-sm text-ink-muted">{t('shopping.household.intro')}</p>
        {members.length === 0 ? (
          <p className="mt-2 text-sm text-warn">{t('shopping.noHousehold')}</p>
        ) : (
          <ul className="mt-2 flex flex-col gap-1">
            {members.map((member) => (
              <li key={member.id} className="text-sm">
                {member.displayName}
                {member.role === 'child' ? (
                  <span className="ml-2 text-xs text-ink-muted">{t('children.badge')}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {!child && householdId && onOpenFamilyDisplay ? (
        <div className="kw-tile flex flex-col gap-2">
          <h3 className="font-medium">{t('family.heading')}</h3>
          <p className="text-xs text-ink-muted">{t('family.intro')}</p>
          <button type="button" className="kw-btn kw-btn-small" onClick={onOpenFamilyDisplay}>
            {t('family.open')}
          </button>
          {kids.length > 0 ? <BoardRewardsSetting /> : null}
        </div>
      ) : null}
      {!child && householdId ? (
        <ChildrenSection householdId={householdId} children={kids} onChanged={onChanged} />
      ) : null}
      {child ? null : <InviteTile />}
      {child ? null : <JoinTile onJoined={onChanged} />}
      {child ? null : <LeaveTile alone={alone} withChildren={kids.length > 0} onLeft={onChanged} />}
    </section>
  );
}

/**
 * Was ein Abhaken am Tablet bucht (27.10.2026) — je Gerät, nur Erwachsene.
 * Ab Werk zählt es für das zuständige Kind; abgeschaltet bucht der Server
 * für Erledigtes am Tablet weder Sterne noch Geld.
 */
function BoardRewardsSetting(): React.JSX.Element {
  const { t } = useTranslation();
  const counts = useLiveQuery(() => readBoardRewards(db), [], true);
  return (
    <div className="mt-2 flex flex-col gap-1">
      <button
        type="button"
        aria-pressed={counts}
        onClick={() => void writeBoardRewards(!counts, db)}
        className="kw-toggle self-start"
      >
        {t('family.rewardsToggle')}
      </button>
      <p className="text-xs text-ink-muted">
        {counts ? t('family.rewardsOn') : t('family.rewardsOff')}
      </p>
    </div>
  );
}

function errorText(error: unknown, t: ReturnType<typeof useTranslation>['t']): string {
  if (error instanceof ApiError) {
    if (error.status === 404) return t('shopping.household.codeInvalid');
    if (error.status === 409) return t('shopping.household.conflict');
    if (error.status === 429) return t('shopping.household.tooMany');
  }
  return t('shopping.household.offline');
}

function InviteTile(): React.JSX.Element {
  const { t } = useTranslation();
  const [invite, setInvite] = useState<{ code: string; expiresAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="kw-tile flex flex-col gap-2">
      <h3 className="font-medium">{t('shopping.household.inviteHeading')}</h3>
      <p className="text-xs text-ink-muted">{t('shopping.household.inviteIntro')}</p>
      {invite ? (
        <p className="flex flex-col gap-1">
          <span className="kw-figure text-3xl select-all">{invite.code}</span>
          <span className="text-xs text-ink-muted">
            {t('shopping.household.inviteExpires', {
              date: new Date(invite.expiresAt).toLocaleDateString(),
            })}
          </span>
        </p>
      ) : null}
      <button
        type="button"
        className="kw-btn kw-btn-quiet kw-btn-small"
        onClick={() => {
          setError(null);
          createInviteCode()
            .then(setInvite)
            .catch((caught: unknown) => setError(errorText(caught, t)));
        }}
      >
        {invite ? t('shopping.household.inviteAgain') : t('shopping.household.invite')}
      </button>
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function JoinTile({ onJoined }: { onJoined: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  const codeId = useId();
  const [code, setCode] = useState('');
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  return (
    <form
      className="kw-tile flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (code.trim() === '') return;
        setMessage(null);
        joinHousehold(code)
          .then((result) => {
            setCode('');
            setMessage({
              ok: true,
              text: t('shopping.household.joined', { count: result.movedItems }),
            });
            onJoined();
          })
          .catch((caught: unknown) => setMessage({ ok: false, text: errorText(caught, t) }));
      }}
    >
      <h3 className="font-medium">{t('shopping.household.joinHeading')}</h3>
      <p className="text-xs text-ink-muted">{t('shopping.household.joinIntro')}</p>
      <label htmlFor={codeId} className="flex flex-col gap-1 text-xs text-ink-muted">
        {t('shopping.household.code')}
        <input
          id={codeId}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          autoCapitalize="characters"
          autoComplete="off"
          maxLength={40}
          className="kw-field text-ink uppercase"
        />
      </label>
      <button type="submit" className="kw-btn kw-btn-small">
        {t('shopping.household.join')}
      </button>
      {message ? (
        <p
          role={message.ok ? 'status' : 'alert'}
          className={`text-xs ${message.ok ? 'text-good' : 'text-danger'}`}
        >
          {message.text}
        </p>
      ) : null}
    </form>
  );
}

function LeaveTile({
  alone,
  withChildren,
  onLeft,
}: {
  alone: boolean;
  withChildren: boolean;
  onLeft: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="kw-tile flex flex-col gap-2">
      <h3 className="font-medium">{t('shopping.household.leaveHeading')}</h3>
      <p className="text-xs text-ink-muted">
        {alone
          ? withChildren
            ? t('shopping.household.leaveLastAdult')
            : t('shopping.household.leaveAlone')
          : t('shopping.household.leaveIntro')}
      </p>
      {confirming ? (
        <div className="flex gap-2">
          <button
            type="button"
            className="kw-btn kw-btn-small flex-1 bg-danger"
            onClick={() => {
              setError(null);
              leaveHousehold()
                .then(() => {
                  setConfirming(false);
                  onLeft();
                })
                .catch((caught: unknown) => setError(errorText(caught, t)));
            }}
          >
            {t('shopping.household.leaveConfirm')}
          </button>
          <button
            type="button"
            className="kw-btn kw-btn-quiet kw-btn-small flex-1"
            onClick={() => setConfirming(false)}
          >
            {t('nutrition.library.cancel')}
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={alone}
          className="kw-btn kw-btn-quiet kw-btn-small"
          onClick={() => setConfirming(true)}
        >
          {t('shopping.household.leave')}
        </button>
      )}
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
